import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  TURNOS_INSTALACAO, partesEmSaoPaulo, rotuloTurno, turnoDaAgenda, valorDoTurno, type TurnoInstalacao,
} from '@/lib/turnoInstalacao';

interface AgendaTurnoInlineProps {
  value: string | null | undefined;
  onSalvar: (valor: string) => Promise<void>;
  obrigatorio?: boolean;
  disabled?: boolean;
}

/**
 * A instalação é agendada por TURNO (Douglas 26/09/2026): data + um dos três
 * turnos da Claro. Grava o início do turno em São Paulo; nunca uma hora solta.
 */
export function AgendaTurnoInline({ value, onSalvar, obrigatorio = false, disabled = false }: AgendaTurnoInlineProps) {
  const salvo = partesEmSaoPaulo(value);
  const turnoSalvo = turnoDaAgenda(value);
  const [data, setData] = useState(salvo?.data ?? '');
  const [turno, setTurno] = useState<TurnoInstalacao | null>(turnoSalvo);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    setData(partesEmSaoPaulo(value)?.data ?? '');
    setTurno(turnoDaAgenda(value));
  }, [value]);

  const salvar = async (novaData: string, novoTurno: TurnoInstalacao | null) => {
    if (!novaData || !novoTurno) return;
    if (salvo?.data === novaData && turnoSalvo?.inicio === novoTurno.inicio) return;
    setSalvando(true);
    try {
      await onSalvar(valorDoTurno(novaData, novoTurno));
    } catch {
      setData(salvo?.data ?? '');
      setTurno(turnoSalvo);
    } finally {
      setSalvando(false);
    }
  };

  const completo = Boolean(salvo && turnoSalvo);
  const semTurnoSalvo = Boolean(salvo && !turnoSalvo);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium flex items-center gap-1.5">
          Data e turno da instalação
          {obrigatorio && (
            <Badge variant="outline" className="text-[10px] px-1 py-0">
              Obrigatório
            </Badge>
          )}
        </Label>
        {salvando ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : completo ? (
          <CheckCircle2 className="h-4 w-4 text-success" />
        ) : obrigatorio ? (
          <AlertCircle className="h-4 w-4 text-destructive" />
        ) : null}
      </div>

      <Input
        type="date"
        value={data}
        disabled={disabled || salvando}
        onChange={(e) => setData(e.target.value)}
        onBlur={(e) => {
          if ((e.relatedTarget as HTMLElement | null)?.closest('[data-turnos-instalacao]')) return;
          salvar(e.target.value, turno);
        }}
        className={cn(completo && 'border-success', obrigatorio && !completo && 'border-destructive')}
      />

      <TurnosInstalacaoSeletor
        turno={turno}
        disabled={disabled || salvando}
        onEscolher={(t) => { setTurno(t); salvar(data, t); }}
      />

      {semTurnoSalvo ? (
        <p className="text-[11px] font-medium text-[#7A2235] dark:text-[#C87484]">
          Agenda gravada às {salvo?.hora}, fora dos turnos. Escolha o turno: o aviso ao cliente sai só com a data até lá.
        </p>
      ) : data && !turno ? (
        <p className="text-[11px] text-[#6B5A4D] dark:text-[#B5A8A8]">Escolha o turno para salvar a agenda.</p>
      ) : null}
    </div>
  );
}

/** Os três turnos da Claro lado a lado. O Inputar da fibra e o Pedido 360 usam o mesmo. */
export function TurnosInstalacaoSeletor({ turno, disabled = false, onEscolher }: {
  turno: TurnoInstalacao | null;
  disabled?: boolean;
  onEscolher: (turno: TurnoInstalacao) => void;
}) {
  return (
    <div data-turnos-instalacao className="flex h-8 items-stretch gap-0.5 rounded-md bg-[#EDE8E0] p-0.5 dark:bg-[#161010]" role="radiogroup" aria-label="Turno da instalação">
      {TURNOS_INSTALACAO.map((t) => {
        const ativo = turno?.inicio === t.inicio;
        return (
          <button
            key={t.inicio}
            type="button"
            role="radio"
            aria-checked={ativo}
            disabled={disabled}
            onClick={() => onEscolher(t)}
            className={cn(
              'flex-1 min-w-0 whitespace-nowrap rounded-[5px] px-1.5 text-[11px] font-semibold tabular-nums transition-colors',
              ativo
                ? 'bg-[#5B0A0D] text-white dark:bg-[#9D2530]'
                : 'text-[#6B5A4D] hover:text-[#5B0A0D] dark:text-[#B5A8A8] dark:hover:text-white',
            )}
          >
            {rotuloTurno(t)}
          </button>
        );
      })}
    </div>
  );
}
